"use client";

import type { Envelope } from "./types";

/*
 * 몰 → BFF 클라이언트.
 *
 * 🔴 **브라우저는 `api.<도메인>` 을 직접 부르지 않습니다.** 연동 키(`X-Api-Key`)가
 *    몰 서버에만 있기 때문입니다 (오너 결정 2026-09-07 #2). 모든 호출은 같은
 *    출처의 `/bff/...` 로 나가고, 거기서 키와 사용자 토큰이 붙습니다.
 *    🔑 덤으로 CORS 가 아예 필요 없고, 하이브리드 웹뷰에서도 같은 코드가 돕니다.
 *
 * 🔴 **오류를 개발자 말로 화면에 흘리지 않습니다.** 주 사용자가 30~70대라
 *    "Failed to fetch" 나 "422 Unprocessable" 이 뜨면 거기서 멈춥니다.
 *    `MallApiError.message` 는 항상 사람이 읽는 한국어입니다.
 */

/** 오류 코드 (`docs/base/07` §7 「오류 코드」). 🔴 번호는 재사용하지 않습니다. */
export const ApiErrorCode = {
  OK: 0,
  UNKNOWN: -1,
  UNAUTHENTICATED: 1001,
  FORBIDDEN: 1002,
  BAD_API_KEY: 1003,
  NOT_FOUND: 2001,
  VALIDATION: 3001,
  RATE_LIMITED: 4001,
  SERVER: 9001,
  /**
   * 🔴 **공급처가 답을 못 준 상태입니다 — «오류» 가 아닙니다.**
   *    `POST /orders` 에서 이 코드가 오면 **결제는 이미 끝났고** 쿠폰 발급만
   *    늦은 것입니다. 관리자가 재시도하면 발급됩니다.
   *    9001(서버 오류)과 번호를 나눈 이유가 정확히 이것입니다 — 뭉치면 몰이
   *    회원에게 «오류가 났습니다» 와 «발급이 지연되고 있습니다» 를 구분해
   *    말할 수 없습니다 (`ApiError::UPSTREAM_UNAVAILABLE`).
   */
  UPSTREAM_UNAVAILABLE: 9002,
} as const;

/**
 * 코드별 기본 문구.
 *
 * 🔑 서버가 준 `message` 를 **먼저** 씁니다 — 그쪽이 상황을 압니다("이미 사용한
 *    쿠폰입니다"). 이 표는 서버가 아무 말도 못 한 경우(네트워크·프록시)의 바닥입니다.
 */
const FALLBACK_MESSAGE: Record<number, string> = {
  [ApiErrorCode.UNKNOWN]: "요청을 처리하지 못했습니다. 잠시 뒤 다시 시도해 주세요.",
  [ApiErrorCode.UNAUTHENTICATED]: "로그인이 필요합니다.",
  [ApiErrorCode.FORBIDDEN]: "이 기능을 사용할 권한이 없습니다.",
  [ApiErrorCode.BAD_API_KEY]: "잠시 연결에 문제가 있습니다. 잠시 뒤 다시 시도해 주세요.",
  [ApiErrorCode.NOT_FOUND]: "찾으시는 내용이 없습니다.",
  [ApiErrorCode.VALIDATION]: "입력하신 내용을 다시 확인해 주세요.",
  [ApiErrorCode.RATE_LIMITED]: "요청이 너무 잦습니다. 잠시 뒤 다시 시도해 주세요.",
  [ApiErrorCode.SERVER]: "서버에 문제가 생겼습니다. 잠시 뒤 다시 시도해 주세요.",
  /* 🔴 «다시 시도해 주세요» 라고 하지 않습니다 — 결제가 이미 끝난 자리입니다. */
  [ApiErrorCode.UPSTREAM_UNAVAILABLE]:
    "발급이 조금 지연되고 있습니다. 잠시 뒤 「내 쿠폰」 에서 확인해 주세요.",
};

const NETWORK_MESSAGE = "연결이 되지 않습니다. 인터넷 상태를 확인하고 다시 시도해 주세요.";

export class MallApiError extends Error {
  /** 봉투의 `error` 값 */
  readonly code: number;
  /** HTTP 상태 코드. 🔴 프록시·모니터링이 읽는 값이라 뭉개지 않았습니다 */
  readonly httpStatus: number;
  /**
   * 검증 오류의 항목별 목록.
   *
   * 🔴 **키가 snake_case 입니다** — 실패 응답의 `data` 는 camelCase 로 바뀌지 않습니다
   *    (`docs/base/07` §7-1). 폼이 보낸 필드 이름과 그대로 대응합니다.
   */
  readonly fields: Record<string, string[]>;

  constructor(message: string, code: number, httpStatus: number, fields: Record<string, string[]> = {}) {
    super(message);
    this.name = "MallApiError";
    this.code = code;
    this.httpStatus = httpStatus;
    this.fields = fields;
  }

  /** 이 항목에 붙은 첫 번째 오류 문구. 없으면 undefined */
  fieldError(name: string): string | undefined {
    return this.fields[name]?.[0];
  }
}

/*
 * 🔴 **401 을 만나면 로그인 화면으로 보냅니다 — `router.push` 로.**
 *    `window.location` 을 쓰면 전체 새로고침이 되고, 하이브리드 앱에서 흰 화면이
 *    번쩍입니다 (AGENTS.md §1 · `page.tsx` 머리말). 그래서 라우터를 여기에
 *    등록해 두고 씁니다 — 셸(`_ui/AppShell.tsx`)이 마운트하며 넣어 줍니다.
 *
 * ⚠️ 등록 전(첫 렌더 직전)에 401 이 나면 이동하지 못합니다. 그 경우에도 세션은
 *    비우므로 화면은 "로그인이 필요합니다" 로 떨어집니다 — 조용히 실패하지 않습니다.
 */
type Navigate = (href: string) => void;
let navigate: Navigate | null = null;

export function registerNavigate(fn: Navigate | null): void {
  navigate = fn;
}

/** 세션이 끊겼을 때 셸이 정리할 것 (`_lib/session.ts` 가 등록합니다). */
type SessionDropped = () => void;
let onSessionDropped: SessionDropped | null = null;

export function registerSessionDropped(fn: SessionDropped | null): void {
  onSessionDropped = fn;
}

export type RequestOptions = {
  /** 쿼리 문자열. `undefined`·`null`·빈 문자열 값은 빠집니다 */
  query?: Record<string, string | number | undefined | null>;
  /** JSON 본문 */
  body?: unknown;
  /**
   * 🔴 로그인·가입 화면은 이것을 켭니다. 그 화면에서 401 은 "비밀번호가 틀렸다"
   *    는 뜻이지 세션이 끊긴 것이 아닙니다 — 로그인 화면에서 로그인 화면으로
   *    다시 보내면 오류 문구가 화면 전환에 지워집니다.
   */
  keepOnUnauthorized?: boolean;
  signal?: AbortSignal;
};

function buildPath(path: string, query?: RequestOptions["query"]): string {
  const clean = path.startsWith("/") ? path : `/${path}`;
  if (!query) return `/bff${clean}`;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `/bff${clean}?${qs}` : `/bff${clean}`;
}

async function request<T>(method: string, path: string, options: RequestOptions = {}): Promise<T> {
  const hasBody = options.body !== undefined;

  let res: Response;
  try {
    res = await fetch(buildPath(path, options.query), {
      method,
      headers: hasBody ? { "Content-Type": "application/json" } : undefined,
      body: hasBody ? JSON.stringify(options.body) : undefined,
      signal: options.signal,
      /* 🔑 쿠키(`tnf_member`)가 같은 출처로 나가야 BFF 가 토큰을 붙입니다. */
      credentials: "same-origin",
      cache: "no-store",
    });
  } catch (cause) {
    /* 🔑 중단(화면을 떠남)은 오류가 아닙니다. 그대로 던져 호출부가 무시하게 둡니다. */
    if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
    throw new MallApiError(NETWORK_MESSAGE, ApiErrorCode.UNKNOWN, 0);
  }

  /* 204 — 본문이 없습니다 (`/track/page-view`). */
  if (res.status === 204) return null as T;

  let envelope: Envelope<T> | null = null;
  try {
    envelope = (await res.json()) as Envelope<T>;
  } catch {
    /*
     * 🔴 봉투가 아닌 응답입니다. nginx 나 웹뷰가 끼어들었다는 뜻이지 API 의 답이
     *    아닙니다 — 여기서 본문을 화면에 뿌리면 HTML 조각이 그대로 보입니다.
     */
    throw new MallApiError(
      res.ok ? NETWORK_MESSAGE : FALLBACK_MESSAGE[ApiErrorCode.SERVER],
      ApiErrorCode.UNKNOWN,
      res.status,
    );
  }

  if (res.ok && envelope.status === "success") {
    return envelope.data as T;
  }

  const code = typeof envelope.error === "number" ? envelope.error : ApiErrorCode.UNKNOWN;
  const message = envelope.message?.trim() || FALLBACK_MESSAGE[code] || FALLBACK_MESSAGE[ApiErrorCode.UNKNOWN];

  /* 검증 오류의 `data` 는 `{필드: [문구…]}` 입니다. 그 외의 `data` 는 무시합니다. */
  const fields: Record<string, string[]> = {};
  if (code === ApiErrorCode.VALIDATION && envelope.data && typeof envelope.data === "object") {
    for (const [key, value] of Object.entries(envelope.data as Record<string, unknown>)) {
      if (Array.isArray(value)) fields[key] = value.map(String);
      else if (typeof value === "string") fields[key] = [value];
    }
  }

  if (res.status === 401 && !options.keepOnUnauthorized) {
    onSessionDropped?.();
    if (navigate && typeof window !== "undefined") {
      const here = `${window.location.pathname}${window.location.search}`;
      /* 🔑 로그인 뒤 돌아올 자리를 들고 갑니다. 로그인 화면 자신은 싣지 않습니다. */
      navigate(here.startsWith("/login") ? "/login" : `/login?next=${encodeURIComponent(here)}`);
    }
  }

  throw new MallApiError(message, code, res.status, fields);
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>("GET", path, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>("POST", path, { ...options, body: body ?? {} }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>("PATCH", path, { ...options, body: body ?? {} }),
  del: <T>(path: string, options?: RequestOptions) => request<T>("DELETE", path, options),
};

/*
 * 🔑 `mediaSrc()` 는 `media.ts`(순수 모듈)로 옮겼습니다 — 서버 `layout.tsx` 가 파비콘 주소를
 *    만들 때도 써야 하는데, 이 파일은 `"use client"` 라 서버에서 부를 수 없습니다.
 *    화면들은 종전처럼 여기서 가져갑니다.
 */
export { mediaSrc } from "./media";

/** 무엇이 잘못됐든 화면에 보여줄 한국어 한 줄을 만들어 줍니다. */
export function humanMessage(error: unknown): string {
  if (error instanceof MallApiError) return error.message;
  if (error instanceof Error && error.name === "AbortError") return "";
  return FALLBACK_MESSAGE[ApiErrorCode.UNKNOWN];
}
